import {
  duplicateEmployerJob,
  transitionEmployerJobLifecycle,
  lifecycleDropdownActions,
  lifecyclePrimaryAction,
  lifecyclePrimaryLabel,
  lifecycleShowsDelete,
  lifecycleSuccessMessage,
} from '@yeshuman/sdk'
import type { LifecycleAction, PublicationStatus } from '@yeshuman/sdk'
import { useState } from 'react'
import { toast } from 'sonner'
import type { QueryClient } from '@tanstack/react-query'

import { deleteOpportunityById, type OrganisationOpportunity } from './use-organisation-opportunities'
import { listOpportunityPublishBlockers } from '../utils/opportunity-publish-readiness'

type UseJobWorkspaceActionsArgs = {
  opportunity: OrganisationOpportunity
  token: string | null
  queryClient: QueryClient
  onPublished?: (detailUrl: string | null) => void
  onAfterTransition?: (action: LifecycleAction) => void
  onDeleted?: () => void
  onDuplicated?: (newOpportunityId: string) => void
}

export function useJobWorkspaceActions({
  opportunity,
  token,
  queryClient,
  onPublished,
  onAfterTransition,
  onDeleted,
  onDuplicated,
}: UseJobWorkspaceActionsArgs) {
  const [busy, setBusy] = useState(false)
  const [pendingConfirm, setPendingConfirm] = useState<LifecycleAction | null>(null)
  const [pendingDelete, setPendingDelete] = useState(false)

  const status = (opportunity.publication_status || 'draft') as PublicationStatus
  const primary = lifecyclePrimaryAction(status)
  const dropdown = lifecycleDropdownActions(status)
  const showDelete = lifecycleShowsDelete(status)
  const primaryLabel = primary ? lifecyclePrimaryLabel(status, primary) : ''
  const publishBlockerItems = primary === 'publish' ? listOpportunityPublishBlockers(opportunity) : []
  const publishBlocked = publishBlockerItems.length > 0
  const publishBlockedTitle = publishBlocked
    ? publishBlockerItems.map((blocker) => blocker.message).join(' ')
    : undefined

  const runAction = async (action: LifecycleAction) => {
    if (!token) return
    setBusy(true)
    try {
      const priorStatus = status
      await transitionEmployerJobLifecycle({
        token,
        opportunityId: opportunity.id,
        action,
        queryClient,
        onPublished: (p) => {
          onPublished?.(p.detail_url || p.public_url || null)
        },
      })
      toast.success(
        priorStatus === 'closed' && action === 'publish'
          ? 'Job re-opened.'
          : lifecycleSuccessMessage(action),
      )
      onAfterTransition?.(action)
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not update job status')
    } finally {
      setBusy(false)
      setPendingConfirm(null)
    }
  }

  const runDelete = async () => {
    if (!token) return
    setBusy(true)
    try {
      await deleteOpportunityById(opportunity.id, token)
      queryClient.removeQueries({ queryKey: ['opportunity', opportunity.id] })
      await queryClient.invalidateQueries({ queryKey: ['organisation-opportunities'] })
      await queryClient.invalidateQueries({ queryKey: ['organisation-opportunity'] })
      await queryClient.invalidateQueries({ queryKey: ['opportunities'] })
      toast.success('Job deleted.')
      onDeleted?.()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not delete job')
    } finally {
      setBusy(false)
      setPendingDelete(false)
    }
  }

  const runDuplicate = async () => {
    if (!token || busy) return
    setBusy(true)
    try {
      const created = await duplicateEmployerJob({
        token,
        opportunityId: opportunity.id,
        queryClient,
      })
      toast.success('Job duplicated.')
      onDuplicated?.(created.id)
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not duplicate job')
    } finally {
      setBusy(false)
    }
  }

  const handleAction = (action: LifecycleAction, needsConfirm: boolean) => {
    if (needsConfirm) {
      setPendingConfirm(action)
      return
    }
    void runAction(action)
  }

  return {
    busy,
    status,
    primary,
    dropdown,
    showDelete,
    primaryLabel,
    publishBlocked,
    publishBlockedTitle,
    publishBlockerItems,
    pendingConfirm,
    setPendingConfirm,
    pendingDelete,
    setPendingDelete,
    runAction,
    runDelete,
    runDuplicate,
    handleAction,
  }
}
